import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Megaphone, 
  Plus, 
  Eye, 
  MousePointer, 
  DollarSign,
  Image,
  Link as LinkIcon,
  Calendar,
  TrendingUp
} from 'lucide-react';

export default function AdsPage() {
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [adType, setAdType] = useState('banner');
  const [budget, setBudget] = useState('');
  const [duration, setDuration] = useState('7');

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    try {
      const response = await api.get('/ads/my-ads');
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAd = async () => {
    if (!title || !description || !mediaUrl || !linkUrl || !budget) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (parseFloat(budget) < 10) {
      toast.error('Minimum budget is $10');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/ads/create', {
        title,
        description,
        media_url: mediaUrl,
        link_url: linkUrl,
        ad_type: adType,
        budget: parseFloat(budget),
        duration_days: parseInt(duration)
      });
      
      setAds(prev => [response.data, ...prev]);
      setShowCreateModal(false);
      resetForm();
      toast.success('Ad created! Proceed to payment to activate.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ad');
    } finally {
      setCreating(false);
    }
  };

  const handlePayForAd = async (adId) => {
    try {
      const response = await api.post(`/ads/${adId}/pay`, {
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start payment');
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setMediaUrl('');
    setLinkUrl('');
    setAdType('banner');
    setBudget('');
    setDuration('7');
  };

  const getStatusBadge = (ad) => {
    if (ad.status === 'pending_payment') {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-500">Pending Payment</span>;
    }
    if (ad.is_active) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-500/20 text-green-500">Active</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-muted text-muted-foreground">Inactive</span>;
  };

  if (loading) {
    return (
      <div className="mg-page max-w-4xl py-6" data-testid="ads-page">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mg-page max-w-4xl py-6" data-testid="ads-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-primary" />
          Advertising
        </h1>
        <Button onClick={() => setShowCreateModal(true)} className="rounded-full" data-testid="create-ad-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Ad
        </Button>
      </div>

      {/* Info Banner */}
      <div className="glass-card p-6 mb-6 bg-gradient-to-r from-primary/10 to-accent/10">
        <h2 className="font-heading font-bold text-lg mb-2">Reach Your Audience</h2>
        <p className="text-muted-foreground text-sm mb-4">
          Create ads to promote your brand, products, or content to Midgard users.
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-primary">$0.001</p>
            <p className="text-xs text-muted-foreground">per impression</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-primary">$0.01</p>
            <p className="text-xs text-muted-foreground">per click</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-primary">$10</p>
            <p className="text-xs text-muted-foreground">min budget</p>
          </div>
        </div>
      </div>

      {/* Ads List */}
      {ads.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-heading text-lg font-bold mb-2">No Ads Yet</h3>
          <p className="text-muted-foreground mb-4">Create your first ad campaign to reach Midgard users.</p>
          <Button onClick={() => setShowCreateModal(true)} className="rounded-full">
            Create Your First Ad
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {ads.map((ad) => (
            <div key={ad.id} className="glass-card p-6">
              <div className="flex gap-4">
                {/* Preview */}
                <div className="w-32 h-24 rounded-xl overflow-hidden bg-muted shrink-0">
                  <img src={ad.media_url} alt="" className="w-full h-full object-cover" />
                </div>
                
                {/* Details */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold">{ad.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">{ad.description}</p>
                    </div>
                    {getStatusBadge(ad)}
                  </div>
                  
                  <div className="flex items-center gap-6 mt-3 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span>{ad.impressions} impressions</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer className="w-4 h-4 text-muted-foreground" />
                      <span>{ad.clicks} clicks</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <span>${ad.spent?.toFixed(2)} / ${ad.budget}</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {ad.status === 'pending_payment' && (
                  <Button onClick={() => handlePayForAd(ad.id)} className="rounded-full shrink-0">
                    Pay ${ad.budget}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Ad Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Create Advertisement
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Ad Title</Label>
              <Input
                placeholder="Your ad title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1"
                maxLength={50}
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                placeholder="What are you promoting?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1"
                rows={2}
                maxLength={150}
              />
            </div>
            
            <div>
              <Label>Media URL (Image)</Label>
              <div className="relative mt-1">
                <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label>Link URL</Label>
              <div className="relative mt-1">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="https://yourwebsite.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label>Ad Type</Label>
              <Select value={adType} onValueChange={setAdType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="banner">Banner Ad</SelectItem>
                  <SelectItem value="sponsored_post">Sponsored Post</SelectItem>
                  <SelectItem value="product_placement">Product Placement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget ($)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder="50"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    className="pl-10"
                    min="10"
                  />
                </div>
              </div>
              
              <div>
                <Label>Duration (days)</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                    className="pl-10"
                    min="1"
                    max="90"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="ghost" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleCreateAd} disabled={creating}>
                {creating ? 'Creating...' : 'Create Ad'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
